import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AZUL, VERDE } from "../theme";

/**
 * Botón "Instalar app" (celular y escritorio).
 *
 * El evento `beforeinstallprompt` lo captura index.js apenas carga la página
 * — mucho antes de que este componente monte — y lo deja en
 * window.__promptInstalacion. Acá se lee de ahí y además se escucha el evento
 * "pwa:instalable" por si llega después. Sin eso el botón no aparecía nunca en
 * escritorio, porque el evento ya había pasado cuando React montaba.
 *
 * Estados posibles:
 *   - Instalable  → botón "Instalar app"
 *   - Ya instalada→ chip "App instalada" (no se ofrece instalar de nuevo)
 *   - iOS         → instrucciones, porque Safari no tiene API de instalación
 */
export default function InstalarAppButton() {
  const { t } = useTranslation();
  const [prompt,    setPrompt]    = useState(() => window.__promptInstalacion || null);
  const [instalada, setInstalada] = useState(false);
  const [ayudaIOS,  setAyudaIOS]  = useState(false);

  const esIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    function onInstalable() { setPrompt(window.__promptInstalacion || null); }
    function onInstalada()  { setPrompt(null); setInstalada(true); }

    window.addEventListener("pwa:instalable", onInstalable);
    window.addEventListener("pwa:instalada", onInstalada);
    onInstalable();

    return () => {
      window.removeEventListener("pwa:instalable", onInstalable);
      window.removeEventListener("pwa:instalada", onInstalada);
    };
  }, []);

  // ¿Ya está instalada? Tres señales, de la más fiable a la más débil:
  //   1. Estamos corriendo dentro de la app instalada (display-mode standalone)
  //   2. getInstalledRelatedApps: Chrome sabe si la PWA está instalada aunque
  //      la estemos viendo desde una pestaña normal
  //   3. La marca que dejamos en localStorage cuando se instaló
  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) { setInstalada(true); return; }

    if (localStorage.getItem("pwa_instalada") === "true") setInstalada(true);

    let cancelado = false;
    navigator.getInstalledRelatedApps?.()
      .then((apps) => { if (!cancelado && apps.length > 0) setInstalada(true); })
      .catch(() => { /* navegador sin soporte: quedan las otras dos señales */ });
    return () => { cancelado = true; };
  }, []);

  async function instalar() {
    if (!prompt) { setAyudaIOS(v => !v); return; }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__promptInstalacion = null;
    setPrompt(null);
    if (outcome === "accepted") {
      try { localStorage.setItem("pwa_instalada", "true"); } catch { /* modo privado */ }
      setInstalada(true);
    }
  }

  // ── Ya instalada ──────────────────────────────────────────────────────────
  if (instalada) {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "8px 14px", borderRadius: 11, fontSize: 13, fontWeight: 700,
        background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.32)",
        color: VERDE, whiteSpace: "nowrap",
      }}>
        <i className="fa-solid fa-circle-check" /> {t("agua.appInstalada")}
      </span>
    );
  }

  // Sin evento de instalación y fuera de iOS no hay nada que ofrecer: o el
  // navegador no soporta PWA, o la página no cumple los requisitos (hace falta
  // HTTPS; en localhost sí funciona).
  if (!prompt && !esIOS) return null;

  return (
    <div style={{ position: "relative" }}>
      <button onClick={instalar} className="agua-btn" style={{
        background: "transparent", color: AZUL,
        border: `1px solid ${AZUL}`, padding: "8px 14px", fontSize: 13,
      }}>
        <i className="fa-solid fa-arrow-down-to-bracket" /> {t("agua.instalarApp")}
      </button>

      {ayudaIOS && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 30,
          width: 260, padding: "12px 14px", borderRadius: 12,
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          boxShadow: "0 12px 32px rgba(0,0,0,.28)", fontSize: 13,
          color: "var(--text-primary)", lineHeight: 1.5,
        }}>
          <strong style={{ display: "block", marginBottom: 6 }}>{t("agua.instalarIOSTitulo")}</strong>
          {t("agua.instalarIOSPasos")}
        </div>
      )}
    </div>
  );
}
